import { useRef, useEffect } from "react";
import { DatePicker } from "@rio-cloud/rio-uikit";
import Card from "@rio-cloud/rio-uikit/Card";
import moment, { Moment } from "moment";
import "moment/locale/pt-br";

moment.locale("pt-br");

interface DateRangePickerProps {
  show: boolean;
  onClose: () => void;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
}

export default function DateRangePicker({
  show,
  onClose,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: DateRangePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        const monthNavigation = target.closest(".month-navigation");
        if (!monthNavigation) {
          onClose();
        }
      }
    };

    if (show) {
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [show, onClose]);

  const handleStartDateChange = (result: Moment | string) => {
    const date = moment(result).format("YYYY-MM-DD");
    onStartDateChange?.(date);
  };

  const handleEndDateChange = (result: Moment | string) => {
    const date = moment(result).format("YYYY-MM-DD");
    if (startDate && moment(date).isBefore(moment(startDate, "YYYY-MM-DD"))) {
      onEndDateChange?.("");
    } else {
      onEndDateChange?.(date);
      if (startDate && date) {
        onClose();
      }
    }
  };

  const getValidDateProps = (isEndDatePicker: boolean) => {
    if (isEndDatePicker && startDate) {
      const minDate = moment(startDate);
      const maxDate = moment(startDate).add(1, "year");
      return {
        isValidDate: (current: Moment) => {
          return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
        },
      };
    }

    if (!isEndDatePicker && endDate) {
      const minDate = moment(endDate).subtract(1, "year");
      const maxDate = moment(endDate);
      return {
        isValidDate: (current: Moment) => {
          return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
        },
      };
    }

    return {};
  };

  const formatDateForDisplay = (date?: string) => {
    if (!date) return "";
    return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  if (!show) return null;

  return (
    <div className="width-100pct" ref={containerRef} onClick={(e) => e.stopPropagation()}>
      <Card className="padding-25 rounded display-flex flex-column gap-20 width-100pct shadow-default bg-white">
        <div className="display-flex flex-row width-100pct gap-20">
          <div className="form-group flex-1-1 margin-0">
            <label className="text-size-14 text-color-black text-medium" htmlFor="start-date-picker">
              Qual o período inicial?
            </label>
            <div className="input-group">
              <span className="input-group-addon">
                <i className="rioglyph rioglyph-calendar" />
              </span>
              <DatePicker
                timeFormat={false}
                id="start-date-picker"
                dateFormat="DD/MM/YYYY"
                className="width-100pct margin-0"
                inputProps={{ placeholder: "DD/MM/AAAA" }}
                onChange={handleStartDateChange}
                value={formatDateForDisplay(startDate)}
                closeOnClickOutside={false}
                initialViewMode="days"
                {...getValidDateProps(false)}
              />
            </div>
          </div>

          <div className="form-group flex-1-1 margin-0">
            <label className="text-size-14 text-color-black text-medium" htmlFor="end-date-picker">
              Qual o período final?
            </label>
            <div className="input-group">
              <span className="input-group-addon">
                <i className="rioglyph rioglyph-calendar" />
              </span>
              <DatePicker
                timeFormat={false}
                id="end-date-picker"
                dateFormat="DD/MM/YYYY"
                className="width-100pct margin-0"
                inputProps={{ placeholder: "DD/MM/AAAA" }}
                onChange={handleEndDateChange}
                value={formatDateForDisplay(endDate)}
                closeOnClickOutside={false}
                initialViewMode="days"
                {...getValidDateProps(true)}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
